/**
 * Session-removed comparator guard helpers for OrderInProgress.
 *
 * A re-hydration on the in-progress page re-runs `overlayRichComparatorsFromRfd`,
 * which re-projects the authoritative `requisitionFormData.comparators` (all
 * relationships). Without a guard, a non-parent the user just removed (an
 * extended relative / sibling) would reappear from that stale source before the
 * removal is persisted on the next Send Updates. These pure helpers let the
 * component record the identity of a removed comparator and strip it from every
 * hydration dispatch until the removal is persisted.
 *
 * Kept in a standalone module (not in `OrderInProgress.tsx`) so the page file
 * only exports its component (React Fast Refresh constraint) and so the helpers
 * are unit-testable in isolation.
 */

import type { CanonicalOrderPayload, Comparator } from '@/ordering/canonicalOrder'

/**
 * Single, best-available stable identity key for a comparator, in order of
 * preference: client `id`, then `demographic.demographicId`, then a
 * relationship + name + DOB composite. Returns `null` when no usable identity
 * exists. Prefer {@link comparatorIdentityKeys} for matching across shapes.
 */
export function comparatorIdentityKey(c: Comparator | null | undefined): string | null {
  const keys = comparatorIdentityKeys(c)
  return keys.length > 0 ? keys[0] : null
}

/**
 * ALL identity keys a comparator can be matched on (client `id`,
 * `demographic.demographicId`, and the relationship+name+dob composite). The
 * row in Redux carries a client `id`, but its RFD re-projection on re-hydration
 * does NOT — the two share only the `demographicId` / composite. Recording every
 * available key on removal, and matching a candidate row if ANY of its keys is
 * in the removed set, bridges those shapes so the removed row is stripped
 * regardless of which identity survives the re-projection.
 */
export function comparatorIdentityKeys(c: Comparator | null | undefined): string[] {
  if (!c) return []
  const keys: string[] = []
  if (typeof c.id === 'string' && c.id.trim() !== '') keys.push(`id:${c.id.trim()}`)
  const demoId = c.demographic?.demographicId
  if (typeof demoId === 'string' && demoId.trim() !== '') keys.push(`demo:${demoId.trim()}`)
  const rel = String(c.relationship ?? '').trim()
  const first = String(c.demographic?.firstName ?? '')
    .trim()
    .toLowerCase()
  const last = String(c.demographic?.lastName ?? '')
    .trim()
    .toLowerCase()
  const dob = String(c.demographic?.dateOfBirth ?? '').trim()
  if (rel && (first || last || dob)) keys.push(`rnd:${rel}|${first}|${last}|${dob}`)
  return keys
}

/**
 * Whether a comparator was ADDED in the current edit session — i.e. none of its
 * identity keys are present in the baseline (last-sent / persisted) key set.
 *
 * Used to let the post-accession editability window edit a freshly-added
 * comparator (e.g. a mother just added during the window) while keeping
 * already-persisted comparators locked. A comparator with no usable identity
 * key (no id / demographic / name+dob) is treated as newly added, since it
 * cannot match a baseline row.
 */
export function isNewlyAddedComparator(c: Comparator | null | undefined, baselineKeys: ReadonlySet<string>): boolean {
  const keys = comparatorIdentityKeys(c)
  if (keys.length === 0) return true
  return !keys.some((k) => baselineKeys.has(k))
}

export interface AddedAfterSubmitContext {
  /** Identity keys of the last-sent baseline (moves forward on every Send Updates). */
  baselineKeys: ReadonlySet<string>
  /** Whether `baselineKeys` was populated for the current order (see baselineComparatorKeysReadyRef). */
  baselineReady: boolean
  /** Sticky keys of comparators first seen as new this page session; never cleared by Send Updates. */
  addedAfterSubmitKeys: ReadonlySet<string>
}

/**
 * GEN-5654: whether a comparator was added AFTER the order's initial submission,
 * which decides if the platform collects its consent to test in
 * `PRACTITIONER_MEDIATED_AB`.
 *
 * The SPA cannot read the initial submission snapshot (the stored canonical order
 * is rewritten by every Send Updates and comparators carry no created/submitted
 * marker), so this is an approximation that errs toward requiring contact:
 * a comparator is "added after submit" when it is not in the last-sent baseline,
 * OR when it was seen as new earlier in this page session (sticky across Send
 * Updates, so the requirement cannot be cleared by sending the update).
 * A missing client `id` alone is not evidence: identity is matched on any key
 * (id, demographicId, name+DOB); only a row with no identity at all (the Add modal
 * before anything is typed) counts as new.
 */
export function isComparatorAddedAfterSubmit(c: Comparator | null | undefined, ctx: AddedAfterSubmitContext): boolean {
  if (!ctx.baselineReady) return c?.id == null
  const keys = comparatorIdentityKeys(c)
  if (keys.some((k) => ctx.addedAfterSubmitKeys.has(k))) return true
  return isNewlyAddedComparator(c, ctx.baselineKeys)
}

/**
 * Fold the comparators that are new relative to `outgoingBaselineKeys` (the baseline
 * about to be replaced by a successful Send Updates) into the sticky
 * added-after-submit key set. The PROBAND self-row is never a comparator add.
 */
export function recordAddedAfterSubmitKeys(
  prior: ReadonlySet<string>,
  comparators: readonly (Comparator | null | undefined)[],
  outgoingBaselineKeys: ReadonlySet<string>
): Set<string> {
  const next = new Set(prior)
  for (const c of comparators) {
    if (!c || c.relationship === 'PROBAND') continue
    if (!isNewlyAddedComparator(c, outgoingBaselineKeys)) continue
    for (const k of comparatorIdentityKeys(c)) next.add(k)
  }
  return next
}

/**
 * A comparator the user removed this session that is pending cancellation
 * (persisted-by-omission on the next Send Updates). Carries the display
 * `label` shown in the Send Updates confirm modal plus ALL identity keys of
 * the removed row so it can be matched back to a later re-add regardless of
 * which identity survives.
 */
export interface PendingComparatorCancellation {
  keys: string[]
  label: string
}

/**
 * True when a candidate row shares ANY identity key with a pending
 * cancellation entry. Add Comparator assigns a fresh client `id` via
 * `genComparatorId`, so a re-added row shares only the `demo:` / `rnd:` keys
 * with the removed one — matching on overlap (not the removed row's primary
 * key) is what lets a remove-then-re-add clear the stale notice.
 */
export function cancellationMatchesKeys(
  entry: PendingComparatorCancellation,
  candidateKeys: readonly string[]
): boolean {
  return entry.keys.some((k) => candidateKeys.includes(k))
}

/**
 * Drop any pending-cancellation entries that match `candidateKeys` (by key
 * overlap). Returns the same array reference when nothing matches so callers
 * can skip a state update. Used both to clear a notice when its comparator is
 * re-added and to de-duplicate before recording a fresh removal.
 */
export function dropMatchingCancellations(
  entries: readonly PendingComparatorCancellation[],
  candidateKeys: readonly string[]
): PendingComparatorCancellation[] {
  const next = entries.filter((entry) => !cancellationMatchesKeys(entry, candidateKeys))
  return next.length === entries.length ? (entries as PendingComparatorCancellation[]) : next
}

/**
 * Drop any comparators sharing an identity key with `removedKeys` from a
 * freshly-hydrated canonical order, so a re-hydration cannot resurrect a
 * comparator the user removed earlier in this session. Pure; returns the same
 * reference when nothing is filtered.
 */
export function stripSessionRemovedComparators(
  co: CanonicalOrderPayload,
  removedKeys: ReadonlySet<string>
): CanonicalOrderPayload {
  if (removedKeys.size === 0 || !Array.isArray(co.comparators)) return co
  const kept = co.comparators.filter((c) => {
    const keys = comparatorIdentityKeys(c)
    return !keys.some((k) => removedKeys.has(k))
  })
  if (kept.length === co.comparators.length) return co
  return { ...co, comparators: kept }
}
